import { DocumentSpec, SalePDFProps } from "../../../_importDocs";

import {
  getColumnWidth,
  lotBaseFields,
} from "@/components/sale-page/lots/price-sheets/PriceEntryDialog";
import { jsPDF } from "jspdf";
import { type Styles } from "jspdf-autotable";

// Components
import header from "../client-side/parts/header";
import table from "../client-side/parts/table";
import footer from "./parts/footer";

// Helpers
import { formatAsCurrency } from "@/data/amounts";
import { getLotsInRange, sortByLotNumber } from "@/data/lots";
import { AttributeDefinition, Lot, Sale } from "types";

const ClerkingSheets: DocumentSpec = {
  // must be unique
  id: "clerking-sheets",

  objectType: "sale",

  // displayed to the user
  name: "Clerking Sheets",

  // when the PDF is downloaded, this function is called to generate the filename
  getFilename: (props) => {
    return `Clerking Sheets`;
  },

  // unused for sales...
  isAvailableFor: () => {
    return true;
  },

  jsPDF: (props: SalePDFProps) => {
    // Need to get the columns from the url
    console.time("jsPDF ClerkingSheets Inner");

    // Document Setup & Configuration
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true,
      putOnlyUsedFonts: true,
    });

    const columns = props.columns ?? {};

    const { sale, market, marketLogo } = props;

    // Sale Title
    const saleTitle = props.sale.name;

    let totalWidth = 0;

    // Loop through the columns and get the width
    Object.keys(columns).forEach((key) => {
      const columnWidth = getColumnWidth(key);
      totalWidth += columnWidth;
    });

    const tableEl = tableData(
      props.lots,
      sale,
      props.lotStart && props.lotEnd ? true : false,
      props.lotStart ?? "",
      props.lotEnd ?? "",
      Object.keys(columns),
      totalWidth,
      doc.internal.pageSize.getWidth()
    );

    // Header
    let pdfTitle = `${saleTitle} | Clerking Sheets`;
    let subTitle = "";

    // Header & Footer Calculations
    header(doc, {
      output: false,
      title: pdfTitle,
      subTitle: subTitle,
      textFillColor: market.secondaryColour ?? "#294F43",
    });

    footer(doc, {
      output: false,
      textFillColor: market.secondaryColour ?? "#294F43",
      marketDetails: {
        logo: marketLogo,
        name: market.name,
        email: market.email,
      },
    });

    table(doc, {
      columns: tableEl.tableHead,
      body: tableEl.tableBody,
      columnStyles: tableEl.columnStyles,
      bodyStyles: {
        minCellHeight: 17,
        valign: "middle",
      },
      headStyles: {
        valign: "bottom",
      },

      headerFillColor: market.secondaryColour ?? "#294F43",
      headerTextColor: "#FFFFFF",
      stripeFillColor: "#EFEFEF",
      stripeTextColor: "#616161",
    });

    // Header Footer
    header(doc, {
      title: pdfTitle,
      subTitle: subTitle,
      output: true,
      textFillColor: market.secondaryColour ?? "#294F43",
    });

    footer(doc, {
      output: true,
      textFillColor: market.secondaryColour ?? "#294F43",
      marketDetails: {
        logo: marketLogo,
        name: market.name,
        email: market.email,
      },
    });

    console.timeEnd("jsPDF ClerkingSheets Inner");

    return doc;
  },
};

function tableData(
  lots: Lot[],
  sale: Sale,
  isRangeEnabled: boolean,
  lotStart: string,
  lotEnd: string,
  selectedColumns: string[],
  currentTableWidth: number,
  pdfWidth: number
) {
  let sortedLots = sortByLotNumber(lots);

  // If we have a range, filter the lots
  if (isRangeEnabled) {
    sortedLots = getLotsInRange(lots, lotStart, lotEnd);
  }

  const tableHead: {
    dataKey: string;
    header: string;
    style?: Partial<Styles>;
  }[] = [];
  const tableBody: { [key: string]: string | number | null }[] = [];

  const columnStyles: { [key: string]: Partial<Styles> } = {};

  // Now we want to format the data for the grid
  sortedLots.map((lot) => {
    // Add the columns to the table body
    let row: { [key: string]: string | number | null } = {};
    selectedColumns.forEach((columnId) => {
      switch (columnId) {
        case "lotNumber":
          row[columnId] = lot.lotNumber ?? "";
          break;
        case "sellerName":
          row[columnId] = lot.seller?.displayName ?? "";
          break;
        case "buyerName":
          row[columnId] = lot.buyer?.displayName ?? "";
          break;
        case "title":
        case "comments":
          row[columnId] = lot.attributes?.[columnId] ?? "";
          break;
        case "catalogueDetailVATApplicable":
          row[columnId] = lot.attributes?.[columnId] === true ? "VAT" : "No";
          break;
        case "unitPriceInCents":
          row[columnId] =
            formatAsCurrency(lot.currency, lot.unitPriceInCents) ?? "";
          break;
        case "reservePriceInCents":
          row[columnId] =
            formatAsCurrency(lot.currency, lot.reservePriceInCents) ?? "";
          break;
        case "generated.description":
          row[columnId] = lot.generated?.description ?? "";
          break;
        default:
          row[columnId] = lot.attributes?.[columnId] ?? "";
          break;
      }
    });

    // Add the row to the table body
    tableBody.push(row);
  });

  // If we are
  let tableSmall = currentTableWidth < pdfWidth;

  // Get the column names
  selectedColumns.forEach((columnId) => {
    tableHead.push({
      dataKey: columnId,
      header: getColumnName(sale, columnId),
    });

    columnStyles[columnId] = {
      cellWidth: getColumnWidth(columnId),
      minCellWidth: getColumnWidth(columnId),
    };
  });

  if (tableSmall) {
    // We need to either find comments or title and expand the column if they are not there then expand the last column
    let found = false;

    selectedColumns.forEach((columnId) => {
      if (["comments", "title", "generated.description"].includes(columnId)) {
        found = true;

        columnStyles[columnId] = {
          ...columnStyles[columnId],
          minCellWidth: undefined,
          cellWidth: "auto",
        };
      }
    });
  }

  return { tableBody, tableHead, columnStyles };
}

function getColumnName(sale: Sale, columnId: string) {
  let attributes: AttributeDefinition[] = sale.attributeSet;
  if (lotBaseFields.includes(columnId)) {
    // Special case for generated.description
    if (columnId === "generated.description") {
      return "Description";
    }

    // Special case for lotNumber
    if (columnId === "lotNumber") {
      return "Lot";
    }

    // Special case for unitPriceInCents
    if (columnId === "unitPriceInCents") {
      return "Price";
    }

    // Special case for reservePriceInCents
    if (columnId === "reservePriceInCents") {
      return "Reserve";
    }

    // Format base field name (convert camelCase to Title Case)
    let name = columnId
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

    if (name.toLocaleLowerCase().endsWith(" in cents")) {
      name = name.slice(0, -9);
    }

    return name;
  } else {
    // Find attribute name
    const attr = attributes.find((a) => a.id === columnId);
    return attr ? attr.name : columnId;
  }
}

export default ClerkingSheets;
